/**
 * The map's whole life in a flight route: is it unfolded, has it ever been,
 * and where was the aeroplane the last time a frame ran.
 *
 * A factory rather than three signals inline in `routes/Fly.tsx`, because it
 * is a self-contained piece of state with one job and the route already runs
 * to the file-size gate's ceiling — the same reason `createRunCommand` and
 * `flightSceneCallbacks` were split out of it before this lane existed.
 */

import { createSignal } from "solid-js";
import { headingAngle, type EncounterState } from "@red-opus/engine";
import { playerSimOf } from "../flight/encounter-views.ts";
import { mapPointOfWorld, type MapPlayer } from "./map-model.ts";

export interface MapSession {
  readonly open: () => boolean;
  /**
   * True once the player has pressed `M` at all. It is what mounts the
   * overlay: the sheet decodes the shell's fonts, palette and header sprite
   * sheet on its first paint, and a flight nobody opens a map on should not
   * pay for any of that.
   */
  readonly everOpened: () => boolean;
  readonly setOpen: (open: boolean) => void;
  readonly player: () => MapPlayer | undefined;
  /**
   * One frame's position fix. Called from the scene's `onFlown` — the hook the
   * Mission Recorder already uses, which carries the whole encounter, so the
   * map needs no callback of its own and `flight-scene.ts` needs no change.
   *
   * Writing this signal at frame rate is free while the map is shut, because
   * nothing is subscribed to it; and once it is open the sim is paused, so no
   * further frames arrive and the sheet holds the fix it was opened on.
   */
  readonly track: (encounter: EncounterState) => void;
}

export function createMapSession(playerFlightId: string): MapSession {
  const [open, setOpen] = createSignal(false);
  const [everOpened, setEverOpened] = createSignal(false);
  const [player, setPlayer] = createSignal<MapPlayer | undefined>(undefined);

  return {
    open,
    everOpened,
    setOpen: (next) => {
      if (next) setEverOpened(true);
      setOpen(next);
    },
    player,
    track: (encounter) => {
      const sim = playerSimOf(encounter, playerFlightId);
      setPlayer({ ...mapPointOfWorld(sim.position), headingRad: headingAngle(sim.orientation) });
    },
  };
}
